/**
* Hero Swiper Section
* - Swiper を動的にロード(初期化時のみ取得)
* - aspect-ratio は CSS で先に決まっているので、JSはレイアウト計算のみ担当
*/
(() => {
'use strict';

const CONFIG = {
    BP: '(min-width: 750px)',
    SWIPER_CSS: 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css',
    SWIPER_JS: 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js',
    SPEED: 520,
    POLL: 30,
    TIMEOUT: 8000,
    DEBOUNCE: 80,
    GAP_PC: 24,
    GAP_SP: 14,
    DELAY: 4500,
};

const bp = window.matchMedia(CONFIG.BP);
let swiperPromise = null;

/* ---------- Swiper ライブラリの遅延ロード ---------- */
const loadSwiper = () => {
    if (window.Swiper) return Promise.resolve();
    if (swiperPromise) return swiperPromise;

    swiperPromise = new Promise((resolve, reject) => {
        if (!document.querySelector('link[data-swiper-css]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = CONFIG.SWIPER_CSS;
            link.media = 'print';
            link.onload = () => { link.media = 'all'; };
            link.dataset.swiperCss = '1';
            document.head.appendChild(link);
        }

        if (document.querySelector('script[data-swiper-js]')) {
            const poll = setInterval(() => {
                if (window.Swiper) {
                    clearInterval(poll);
                    resolve();
                }
            }, CONFIG.POLL);
            setTimeout(() => {
                clearInterval(poll);
                reject(new Error('Swiper load timeout'));
            }, CONFIG.TIMEOUT);
            return;
        }

        const script = document.createElement('script');
        script.src = CONFIG.SWIPER_JS;
        script.defer = true;
        script.dataset.swiperJs = '1';
        script.onload = resolve;
        script.onerror = () => reject(new Error('Swiper script error'));
        document.head.appendChild(script);
    });

    return swiperPromise;
};

/* ---------- セクション単位の初期化 ---------- */
const initWrap = async (wrap) => {
    const root = wrap.querySelector('[data-hs]');
    if (!root) return;

    const sid = root.dataset.sectionId;
    const swiperEl = document.getElementById(`hs-swiper-${sid}`);
    const slideCount = Number(wrap.dataset.slideCount || 0);

    const getMode = () => bp.matches ? wrap.dataset.modePc : wrap.dataset.modeSp;
    const getGap = () => Number(
        bp.matches
            ? (root.dataset.gapPc || CONFIG.GAP_PC)
            : (root.dataset.gapSp || CONFIG.GAP_SP)
    );
    const getSlideWidth = () => Number(
        bp.matches ? wrap.dataset.slideWidthPc : wrap.dataset.slideWidthSp
    ) || 0;

    wrap.dataset.mode = getMode();

    // スライド1枚以下なら Swiper を起動しない
    if (slideCount <= 1) { root.classList.remove('is-peek'); wrap.classList.add('is-ready'); return; } try {
        await
            loadSwiper();
    } catch (err) {
        console.error('[Hero Swiper] Swiper load failed:', err);
        wrap.classList.add('is-ready'); return;
    } const prevBtn = root.querySelector('[data-hs-prev]'); const
        nextBtn = root.querySelector('[data-hs-next]'); const dotsWrap = root.querySelector('[data-hs-dots]'); const
            autoplay = root.dataset.autoplay === 'true'; const delay = Number(root.dataset.delay || CONFIG.DELAY); let
                swiper = null; const destroy = () => {
                    try {
                        swiper?.destroy(true, true);
                    } catch (_) { /* noop */ }
                    swiper = null;
                };

    /* ---------- Swiper インスタンスのセットアップ ---------- */
    const setup = () => {
        const mode = getMode();
        const isPeek = mode === 'full_peek';
        const gap = getGap();
        const slideWidthPx = getSlideWidth();

        wrap.dataset.mode = mode;
        root.style.setProperty('--hs-gap', `${gap}px`);

        swiperEl.querySelectorAll('.hs__slide').forEach((el) => {
            el.style.width = (isPeek && slideWidthPx > 0) ? `${slideWidthPx}px` : '';
        });

        root.classList.toggle('is-peek', isPeek);

        // ドット再生成
        if (dotsWrap) dotsWrap.innerHTML = '';
        const dots = [];
        for (let i = 0; i < slideCount; i++) {
            const btn = document.createElement('button'); btn.type = 'button';
            btn.className = 'hs__dot'; btn.addEventListener('click', () => swiper?.slideToLoop(i, CONFIG.SPEED));
            dotsWrap?.appendChild(btn);
            dots.push(btn);
        }

        swiper = new Swiper(swiperEl, {
            loop: true,
            speed: CONFIG.SPEED,
            navigation: { prevEl: prevBtn, nextEl: nextBtn },
            autoplay: autoplay
                ? { delay, disableOnInteraction: false, pauseOnMouseEnter: true }
                : false,
            centeredSlides: true,
            slidesPerView: isPeek ? 'auto' : 1,
            spaceBetween: gap,
            slidesPerGroup: 1,
            loopAdditionalSlides: 0,
            watchSlidesProgress: true,
            watchOverflow: true,
            observer: true,
            observeParents: true,
        });

        const updateDots = () => {
            const active = swiper.realIndex % slideCount;
            dots.forEach((d, i) => d.classList.toggle('is-active', i === active));
        };
        updateDots();
        swiper.on('slideChange', updateDots);

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                swiper.update();
                wrap.classList.add('is-ready');
            });
        });
    };

    setup();

    /* ---------- ブレークポイント変更時の再構築 ---------- */
    let resizeTimer;
    const onBP = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            destroy();
            setup();
        }, CONFIG.DEBOUNCE);
    };

    if (bp.addEventListener) {
        bp.addEventListener('change', onBP);
    } else {
        bp.addListener(onBP);
    }

    /* ---------- Shopifyテーマエディタの再ロード対応 ---------- */
    const onSectionEvent = (e) => {
        if (e.target?.querySelector?.(`[data-section-id="${sid}"]`)) {
            destroy();
            setup();
        }
    };
    document.addEventListener('shopify:section:load', onSectionEvent);
    document.addEventListener('shopify:section:select', onSectionEvent);
};

const boot = () => {
    document.querySelectorAll('[data-hs-wrap]').forEach(initWrap);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
})();